import { Terminal } from "@stripe/terminal-js";
import { Alert, Button, EmvImage, flash, Modal, Tag } from "@xola/ui-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Except } from "type-fest";
import { EmvDevice, Purchase, Seller } from "types";
import { buildCartDisplayInfo } from "../../helpers/stripeTerminalDisplayHelper";
import { useSeller } from "../../hooks/useAccount";
import { EMVTerminal, useStripeTerminal } from "../../hooks/useStripeTerminal";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { SecondaryButton } from "../Common/SecondaryButton";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { PaymentInputRef, PaymentInputRefParams } from "./PaymentInputRef";

interface EmvInputProps {
    className?: string;
}

const defaultProcessingStatus: { message: string; status: string } = {
    message: "Waiting for input",
    status: "primary",
};

export const EmvInput = forwardRef<PaymentInputRef, EmvInputProps>(({ className }, ref) => {
    const [processing, setProcessing] = useState(defaultProcessingStatus);
    const clientSecretRef = useRef<string>();
    const resolveConfirmRef = useRef<(value?: unknown) => void>();
    const rejectConfirmRef = useRef<(value?: unknown) => void>();

    const isManual = useCreatePurchaseStore((state) => !state.isEMV);
    const [isEMVModalOpen, setIsEMVModalOpen] = useState(false);

    const readerTerminal = useStripeTerminal();

    useEffect(() => {
        if (!isManual) {
            setIsEMVModalOpen(false);
        }
    }, [isManual]);

    useImperativeHandle(ref, () => ({
        async prepare() {
            return true;
        },

        async tokenize() {
            return null;
        },

        async confirm(params?: PaymentInputRefParams) {
            clientSecretRef.current = params?.clientSecret;

            return new Promise((resolve, reject) => {
                resolveConfirmRef.current = resolve;
                rejectConfirmRef.current = reject;
                tryProcess();
            });
        },

        closeEMVModal() {
            setIsEMVModalOpen(false);
        },
    }));

    const tryProcess = async () => {
        const clientSecret = clientSecretRef.current;

        let count = 1;
        while (getReaderStatus(readerTerminal) !== EMVTerminal.READER_CONNECTED && count < 3) {
            console.log(`EMV: Terminal is ${getReaderStatus(readerTerminal)}, wait and try again. Count: ${count}`);
            await new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });
            count++;
        }

        if (getReaderStatus(readerTerminal) !== EMVTerminal.READER_CONNECTED) {
            // If reader is still not ready, then abort
            flash.warning(
                "Could not communicate with the Reader. Please make sure your reader is online and on the same network as your device. ",
            );
            return handleCancel();
        }

        if (readerTerminal && clientSecret) {
            setIsEMVModalOpen(true);

            let wasCanceled = false;
            try {
                await processEMVPayment(readerTerminal, clientSecret, setProcessing);
                resolveConfirmRef.current?.();
                console.log("%cEMV: processEMVPayment is complete", "color:green");
            } catch (error: any) {
                if (error.code === "canceled") {
                    wasCanceled = true;
                } else {
                    setProcessing({ message: error.message, status: "danger" });
                    console.error("EMV: Error processing EMV payment", error);
                }
            } finally {
                // Only clear display if payment was successful or had a non-cancel error
                // If user canceled, let useStripeTerminalDisplay hook manage the display
                if (!wasCanceled) {
                    try {
                        await readerTerminal?.clearReaderDisplay();
                        console.log("%cEMV: Cleared reader display", "color:green");
                    } catch (error) {
                        console.warn("EMV: Failed to clear reader display", error);
                    }
                }
            }
        } else {
            console.error("EMV: Can't process EMV payment, missing reader or clientSecret", {
                readerTerminal,
                clientSecret,
            });
            setProcessing({ message: "Error, cannot process payment", status: "danger" });
        }
    };

    const handleCancel = async () => {
        setProcessing({ message: "EMV: Cancelling transaction", status: "warning" });

        try {
            await readerTerminal?.cancelCollectPaymentMethod();
        } catch {} // We don't care if it fails, an error may come if you try to cancel an un-captured transaction

        // Restore cart display after cancellation since purchase is still active
        await restoreCartDisplay();

        setIsEMVModalOpen(false);
        setProcessing(defaultProcessingStatus);
        rejectConfirmRef.current?.();
    };

    const restoreCartDisplay = useCallback(async () => {
        if (!readerTerminal) return;

        try {
            const { items, currency, amount } = usePurchaseResponseStore.getState().purchase;
            if (items?.length && currency && amount !== undefined) {
                const displayInfo = buildCartDisplayInfo({ items, currency, amount } as Purchase);
                await readerTerminal.setReaderDisplay(displayInfo);
                console.log("%cEMV: Restored cart display after cancellation", "color: green", displayInfo);
            }
        } catch (error) {
            console.warn("EMV: Failed to restore cart display after cancellation", error);
        }
    }, [readerTerminal]);

    const handleRetry = async () => {
        setProcessing(defaultProcessingStatus); // Set default status so user knows they can start retrying
        console.log("EMV: Retrying transaction with clientSecret", clientSecretRef.current);
        if (getReaderStatus(readerTerminal) === EMVTerminal.WAITING) {
            try {
                await readerTerminal?.cancelCollectPaymentMethod();
            } catch {}
        }

        await tryProcess();
    };

    return (
        <div className={className}>
            <EMVPaymentModal
                isOpen={isEMVModalOpen}
                processing={processing}
                onCancel={handleCancel}
                onRetry={handleRetry}
            />
        </div>
    );
});

interface EMVPaymentModalProps {
    isOpen: boolean;
    onCancel: () => void;
    onRetry: () => void;
    processing: { message: string; status: string };
}

const EMVPaymentModal = ({ isOpen, onCancel, onRetry, processing }: EMVPaymentModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onCancel}>
            <EmvModalContent processing={processing} onCancel={onCancel} onRetry={onRetry} />
        </Modal>
    );
};

const EmvModalContent = ({ processing, onCancel, onRetry }: Except<EMVPaymentModalProps, "isOpen">) => {
    const seller = useSeller();
    const pairedDevice = getPairedDevice(seller);
    const isSimulated = !!window.flags?.stripe?.simulateTerminal;
    const label = isSimulated ? "Reader Simulator" : pairedDevice?.label ?? `Unknown ${pairedDevice?.id}`;
    const hasFailed = processing.status === "danger";
    const isSuccess = processing.status === "success";

    return (
        <>
            <Modal.Header>Make Payment</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col items-center space-y-4 text-center">
                    <EmvImage />
                    {hasFailed ? (
                        <Alert color={processing.status}>{processing.message}</Alert>
                    ) : (
                        <>
                            <CountdownTimer onComplete={onCancel} />
                            <Tag size="medium" color={processing.status}>
                                Status: {processing.message}
                            </Tag>
                        </>
                    )}

                    <h2>Complete payment using {label}</h2>
                    <div>You have five minutes to complete this transaction after which the booking will expire.</div>
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                {hasFailed && <SecondaryButton onClick={onRetry}>Use Another Card</SecondaryButton>}
                <Button color="danger" disabled={isSuccess} onClick={onCancel}>
                    Cancel Transaction
                </Button>
            </Modal.Footer>
        </>
    );
};

export const getPairedDevice = (seller: Seller) => {
    const devices: EmvDevice[] = seller.preferences.stripeTerminal?.paymentDevices ?? [];
    const computer = JSON.parse(localStorage.getItem("computer") ?? "{}");
    const isSimulated = window.flags?.stripe?.simulateTerminal;
    if (computer.id) {
        return devices.find((d) => d.computer?.id === computer.id && !isSimulated);
    }

    return null;
};

const CountdownTimer = ({ onComplete }: { onComplete: () => void }) => {
    const [seconds, setSeconds] = useState(300);

    useEffect(() => {
        let decrementTimer: NodeJS.Timeout;
        if (seconds > 0) {
            decrementTimer = setTimeout(() => {
                setSeconds(seconds - 1);
            }, 1000);
        } else if (seconds === 0) {
            onComplete();
        }

        return () => {
            if (decrementTimer) {
                clearTimeout(decrementTimer);
            }
        };
    }, [seconds, onComplete]);

    return (
        <h1 className={clsx(seconds === 0 && "text-danger")}>{dayjs.duration(seconds, "seconds").format("mm:ss")}</h1>
    );
};

const processEMVPayment = async (
    readerTerminal: Terminal,
    clientSecret: string,
    setProcessing: ({ message, status }: { message: string; status: string }) => void,
) => {
    console.log(
        "%cEMV: Doing `readerTerminal.collectPaymentMethod`",
        "color: green",
        readerTerminal.getPaymentStatus(),
    );

    const collectPaymentMethodResponse = await readerTerminal.collectPaymentMethod(clientSecret);
    setProcessing({ message: "Card input received, processing payment", status: "primary" });

    if ("paymentIntent" in collectPaymentMethodResponse) {
        const processPaymentResponse = await readerTerminal.processPayment(collectPaymentMethodResponse.paymentIntent);
        console.log("%cEMV: processEMVPayment Processing payment complete", "color:green", processPaymentResponse);

        if ("error" in processPaymentResponse) {
            throw processPaymentResponse.error as Error;
        }

        setProcessing({ message: "Payment received, finalizing order", status: "success" });
    } else {
        throw collectPaymentMethodResponse.error as Error;
    }
};

export const getReaderStatus = (readerTerminal: Terminal): EMVTerminal => {
    return readerTerminal?.getConnectionStatus() as unknown as EMVTerminal;
};
